import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Search, X, Briefcase,
  MapPin, Building2, Clock, DollarSign, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTRACT_TYPES = [
  'CDI', 'CDD', 'CDI ou CDD', 'CDD 6 mois evolutif CDI',
  'CDI - Temps plein', 'CDI ou CDD - Temps plein ou partiel',
  'Freelance', 'Freelance (prestataire indépendante patentee)',
  'Stage', 'Alternance'
];

const emptyOffer = {
  title: '', company: '', department: '', location: 'Papeete, Polynésie française',
  contract_type: 'CDI', salary: '', start_date: '', deadline: '',
  summary: '', missions: [], profile: [], conditions: [],
  contact_email: 'contact@groupeaerhpolynesie.com', tags: [], published: true
};

export const AdminJobOffersTab = ({ token }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyOffer });
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // Temp inputs for list fields
  const [missionInput, setMissionInput] = useState('');
  const [profileInput, setProfileInput] = useState('');
  const [conditionInput, setConditionInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/job-offers`, { headers });
      setOffers(data);
    } catch {
      toast.error('Erreur chargement des offres');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyOffer });
    resetInputs();
    setDialogOpen(true);
  };

  const openEdit = (offer) => {
    setEditing(offer.id);
    setForm({
      title: offer.title || '',
      company: offer.company || '',
      department: offer.department || '',
      location: offer.location || '',
      contract_type: offer.contract_type || 'CDI',
      salary: offer.salary || '',
      start_date: offer.start_date || '',
      deadline: offer.deadline || '',
      summary: offer.summary || '',
      missions: offer.missions || [],
      profile: offer.profile || [],
      conditions: offer.conditions || [],
      contact_email: offer.contact_email || 'contact@groupeaerhpolynesie.com',
      tags: offer.tags || [],
      published: offer.published ?? true
    });
    resetInputs();
    setDialogOpen(true);
  };

  const resetInputs = () => {
    setMissionInput('');
    setProfileInput('');
    setConditionInput('');
    setTagInput('');
  };

  const addToList = (field, value, setter) => {
    const v = value.trim();
    if (v && !form[field].includes(v)) {
      setForm({ ...form, [field]: [...form[field], v] });
    }
    setter('');
  };

  const removeFromList = (field, index) => {
    setForm({ ...form, [field]: form[field].filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.company.trim()) {
      toast.error('Le titre et l\'entreprise sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/job-offers/${editing}`, form, { headers });
        toast.success('Offre mise à jour');
      } else {
        await axios.post(`${API}/admin/job-offers`, form, { headers });
        toast.success('Offre creee');
      }
      setDialogOpen(false);
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await axios.delete(`${API}/admin/job-offers/${id}`, { headers });
      toast.success('Offre supprimee');
      fetchOffers();
    } catch {
      toast.error('Erreur de suppression');
    }
  };

  const togglePublish = async (offer) => {
    try {
      await axios.put(`${API}/admin/job-offers/${offer.id}`, { published: !offer.published }, { headers });
      toast.success(offer.published ? 'Offre depubliee' : 'Offre publiee');
      fetchOffers();
    } catch {
      toast.error('Erreur');
    }
  };

  const filtered = offers.filter(o =>
    !searchFilter ||
    o.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
    o.company.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const ListEditor = ({ label, field, items, inputValue, setInputValue, placeholder }) => (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addToList(field, inputValue, setInputValue))}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button type="button" variant="outline" onClick={() => addToList(field, inputValue, setInputValue)} className="border-[#0D9488] text-[#0D9488] flex-shrink-0">
          Ajouter
        </Button>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((item, i) => (
            <li key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-1.5 group">
              <span className="w-1.5 h-1.5 bg-[#0D9488] rounded-full flex-shrink-0" />
              <span className="flex-1 text-slate-700">{item}</span>
              <button onClick={() => removeFromList(field, i)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div data-testid="admin-job-offers-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Filtrer les offres..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
              data-testid="job-admin-search"
            />
          </div>
          <span className="text-sm text-slate-500">
            {offers.filter(o => o.published).length} publiee{offers.filter(o => o.published).length > 1 ? 's' : ''} / {offers.length} total
          </span>
        </div>
        <Button onClick={openCreate} className="bg-[#0D9488] hover:bg-[#0F766E] text-white" data-testid="job-create-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle offre
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg">Aucune offre</p>
          <p className="text-sm mt-1">Cliquez sur "Nouvelle offre" pour commencer</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((offer) => (
            <div
              key={offer.id}
              className={`flex items-center gap-4 p-4 bg-white rounded-lg border transition-colors ${
                offer.published ? 'border-gray-200 hover:border-gray-300' : 'border-dashed border-gray-300 opacity-60'
              }`}
              data-testid={`job-row-${offer.id}`}
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-[#0D9488]/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-[#0D9488]" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-[#1F2937] truncate">{offer.title}</h4>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                  <span className="flex items-center gap-1 font-medium text-[#0D9488]">
                    <Building2 className="w-3 h-3" /> {offer.company}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {offer.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {offer.contract_type}
                  </span>
                  {offer.salary && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> {offer.salary}
                    </span>
                  )}
                </div>
                {offer.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {offer.tags.map(tag => (
                      <span key={tag} className="bg-slate-100 text-slate-600 text-[10px] px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost" size="icon"
                  onClick={() => togglePublish(offer)}
                  title={offer.published ? 'Depublier' : 'Publier'}
                  data-testid={`job-toggle-${offer.id}`}
                >
                  {offer.published ? (
                    <Eye className="w-4 h-4 text-green-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(offer)} data-testid={`job-edit-${offer.id}`}>
                  <Edit2 className="w-4 h-4 text-blue-500" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(offer.id)} data-testid={`job-delete-${offer.id}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#0D9488]" />
              {editing ? 'Modifier l\'offre' : 'Nouvelle offre d\'emploi'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Title + Company */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Titre du poste *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Juriste Contentieux H/F"
                  className="mt-1"
                  data-testid="job-form-title"
                />
              </div>
              <div>
                <Label>Entreprise *</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Ex: EOS France"
                  className="mt-1"
                  data-testid="job-form-company"
                />
              </div>
            </div>

            {/* Department + Location */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Service / Département</Label>
                <Input
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder="Ex: Direction Juridique"
                  className="mt-1"
                  data-testid="job-form-department"
                />
              </div>
              <div>
                <Label>Localisation</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Ex: Papeete, Polynésie française"
                  className="mt-1"
                  data-testid="job-form-location"
                />
              </div>
            </div>

            {/* Contract + Salary */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Type de contrat</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="job-form-contract">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Remuneration</Label>
                <Input
                  value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                  placeholder="Ex: 250 000 a 320 000 F CFP brut/mois"
                  className="mt-1"
                  data-testid="job-form-salary"
                />
              </div>
            </div>

            {/* Start date + Deadline */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Date de prise de poste</Label>
                <Input
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  placeholder="Ex: Debut aout 2026"
                  className="mt-1"
                  data-testid="job-form-start"
                />
              </div>
              <div>
                <Label>Date limite de candidature</Label>
                <Input
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  placeholder="Ex: 30 avril 2026"
                  className="mt-1"
                  data-testid="job-form-deadline"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label>Email de contact</Label>
              <Input
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="contact@groupeaerhpolynesie.com"
                className="mt-1"
                data-testid="job-form-email"
              />
            </div>

            {/* Summary */}
            <div>
              <Label>Resume du poste</Label>
              <textarea
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                rows={3}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent resize-y"
                placeholder="Description courte du poste..."
                data-testid="job-form-summary"
              />
            </div>

            {/* Missions */}
            <ListEditor
              label="Missions"
              field="missions"
              items={form.missions}
              inputValue={missionInput}
              setInputValue={setMissionInput}
              placeholder="Ex: Rédiger et diffuser des offres d'emploi"
            />

            {/* Profile */}
            <ListEditor
              label="Profil recherche"
              field="profile"
              items={form.profile}
              inputValue={profileInput}
              setInputValue={setProfileInput}
              placeholder="Ex: Bac+3 en RH ou droit social"
            />

            {/* Conditions */}
            <ListEditor
              label="Conditions"
              field="conditions"
              items={form.conditions}
              inputValue={conditionInput}
              setInputValue={setConditionInput}
              placeholder="Ex: Teletravail partiel possible"
            />

            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('tags', tagInput, setTagInput))}
                  placeholder="Ex: RH, CDI, Papeete"
                  className="flex-1"
                  data-testid="job-form-tag-input"
                />
                <Button type="button" variant="outline" onClick={() => addToList('tags', tagInput, setTagInput)} className="border-[#0D9488] text-[#0D9488]">
                  Ajouter
                </Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded-full">
                      {tag}
                      <button onClick={() => removeFromList('tags', i)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Published toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-[#0D9488] focus:ring-[#0D9488]"
                data-testid="job-form-published"
              />
              <span className="text-sm text-gray-700">Publier immédiatement</span>
            </label>

            {/* Save button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white py-5"
              data-testid="job-form-save"
            >
              {saving ? 'Enregistrement...' : (editing ? 'Mettre à jour l\'offre' : 'Créer l\'offre')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobOffersTab;
